/**
 * @fileoverview Profile modal sessions list.
 * @description The "Where you're signed in" block of the profile modal's Security tab: the caller's
 * own live sessions (GET /identity/sessions) as device rows with a badge on the current one and a
 * per-row revoke (DELETE /identity/sessions/{id}), plus a single "Log out of all sessions" action
 * (POST /identity/logout-all). Same endpoints and hooks as SettingsPage's ActiveSessionsPanel,
 * restyled for the modal; a revoke's pending state is read off the shared mutation's `variables`
 * since only one revoke can plausibly be in flight at a time.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useTranslation } from 'react-i18next'

import { useLogoutAllSessions } from '@/features/auth/hooks/useLogoutAllSessions'
import { useRevokeSession } from '@/features/auth/hooks/useRevokeSession'
import { useSessions } from '@/features/auth/hooks/useSessions'
import Badge from '@/shared/components/atoms/Badge'
import LinkButton from '@/shared/components/atoms/LinkButton'
import Skeleton from '@/shared/components/atoms/Skeleton'
import DetailSectionHeading from '@/shared/components/molecules/DetailSectionHeading'

import type { SessionSummary } from '@/features/auth/services/session.service'

interface ProfileSessionRowProps {
  session: SessionSummary
  isRevoking: boolean
  onRevoke: (sessionId: string) => void
}

/**
 * @description One session's device name, last-activity time and revoke action. The current
 * session shows a badge instead of a revoke, since a caller can't revoke the request they're
 * making the revoke with.
 * @param {ProfileSessionRowProps} props - The session and its row-local revoke state/handler.
 * @param {SessionSummary} props.session - The session to render.
 * @param {boolean} props.isRevoking - Whether this row's revoke request is in flight.
 * @param {(sessionId: string) => void} props.onRevoke - Revokes a session by its id.
 */
function ProfileSessionRow({ session, isRevoking, onRevoke }: ProfileSessionRowProps) {
  const { t } = useTranslation()

  /**
   * @description Revokes this row's session.
   */
  const revoke = () => onRevoke(session.sessionId)

  return (
    <li
      className="flex items-center gap-3.5 border-b border-divider py-3 last:border-b-0"
      data-testid={`profile-session-${session.sessionId}`}
    >
      <div
        aria-hidden="true"
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-card bg-surface-muted text-slate"
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <rect x="2" y="3" width="20" height="14" rx="2" />
          <path d="M8 21h8M12 17v4" />
        </svg>
      </div>
      <div className="min-w-0 flex-1">
        <p className="flex items-center gap-2 text-sm font-semibold text-ink">
          <span className="truncate" data-testid={`profile-session-${session.sessionId}-device`}>
            {session.deviceName ?? t('settings.sessions.unknownDevice')}
          </span>
          {session.isCurrent && (
            <Badge
              variant="success"
              data-testid={`profile-session-${session.sessionId}-current-badge`}
            >
              {t('settings.sessions.currentBadge')}
            </Badge>
          )}
        </p>
        <p
          className="mt-0.5 text-compact-sm text-slate"
          data-testid={`profile-session-${session.sessionId}-last-active`}
        >
          {t('settings.sessions.lastActivePrefix')}{' '}
          {new Date(session.lastActivityAt).toLocaleString()}
        </p>
      </div>
      {!session.isCurrent && (
        <LinkButton
          size="sm"
          onClick={revoke}
          disabled={isRevoking}
          data-testid={`profile-session-${session.sessionId}-revoke-button`}
        >
          {isRevoking ? t('settings.sessions.revoking') : t('settings.sessions.revoke')}
        </LinkButton>
      )}
    </li>
  )
}

/**
 * @component
 */
function ProfileSessionsList() {
  const { t } = useTranslation()
  const sessionsQuery = useSessions()
  const revokeMutation = useRevokeSession()
  const logoutAllMutation = useLogoutAllSessions()
  const sessions = sessionsQuery.data ?? []

  /**
   * @description Logs out of every session.
   */
  const logoutAll = () => {
    revokeMutation.reset()
    logoutAllMutation.mutate()
  }

  /**
   * @description Revokes one session, clearing any earlier failure message first.
   * @param {string} sessionId - The session to revoke.
   */
  const revoke = (sessionId: string) => {
    logoutAllMutation.reset()
    revokeMutation.mutate(sessionId)
  }

  return (
    <section className="flex flex-col gap-2" data-testid="profile-sessions">
      <div className="flex items-center justify-between gap-3">
        <DetailSectionHeading>{t('profile.security.sessionsTitle')}</DetailSectionHeading>
        <LinkButton
          size="sm"
          onClick={logoutAll}
          disabled={logoutAllMutation.isPending}
          data-testid="profile-sessions-logout-all-button"
        >
          {logoutAllMutation.isPending
            ? t('settings.sessions.loggingOutAll')
            : t('settings.sessions.logoutAll')}
        </LinkButton>
      </div>

      {sessionsQuery.isPending && (
        <div
          className="flex flex-col gap-3 py-2"
          role="status"
          aria-label={t('layout.loading')}
          data-testid="profile-sessions-loading"
        >
          <Skeleton height={40} />
          <Skeleton height={40} />
        </div>
      )}

      {sessionsQuery.isError && (
        <p role="alert" className="text-sm text-error" data-testid="profile-sessions-error">
          {t('profile.security.sessionsError')}
        </p>
      )}

      {(revokeMutation.isError || logoutAllMutation.isError) && (
        <p role="alert" className="text-sm text-error" data-testid="profile-sessions-action-error">
          {t('profile.security.sessionActionError')}
        </p>
      )}

      {sessionsQuery.isSuccess && sessions.length === 0 && (
        <p className="text-sm text-slate" data-testid="profile-sessions-empty">
          {t('settings.sessions.empty')}
        </p>
      )}

      {sessions.length > 0 && (
        <ul data-testid="profile-sessions-list">
          {sessions.map((session) => (
            <ProfileSessionRow
              key={session.sessionId}
              session={session}
              isRevoking={
                revokeMutation.isPending && revokeMutation.variables === session.sessionId
              }
              onRevoke={revoke}
            />
          ))}
        </ul>
      )}
    </section>
  )
}

export default ProfileSessionsList
